import { useState, useEffect } from 'react';
import type { Tournament, LeaderboardEntry } from '../types';
import { getLeaderboard } from '../services/api';

interface LeaderboardPageProps {
  tournament: Tournament;
}

export default function LeaderboardPage({ tournament }: LeaderboardPageProps) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const result = await getLeaderboard(tournament);
        setLeaderboard(result.leaderboard);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [tournament]);

  const getSkillBadgeClass = (skill: number) => {
    switch (skill) {
      case 1: return 'badge-beginner';
      case 2: return 'badge-intermediate';
      case 3: return 'badge-advanced';
      default: return '';
    }
  };

  const getSkillLabel = (skill: number) => {
    switch (skill) {
      case 1: return 'B';
      case 2: return 'I';
      case 3: return 'A';
      default: return '?';
    }
  };

  if (loading) {
    return <div className="container">Loading leaderboard...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <h1>Leaderboard</h1>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <h1>Live Leaderboard</h1>

      <div className="card">
        <div className="table-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="table" style={{ minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>Rank</th>
                <th>Player</th>
                <th style={{ textAlign: 'center' }}>Skill</th>
                <th style={{ textAlign: 'center' }}>W</th>
                <th style={{ textAlign: 'center' }}>L</th>
                <th style={{ textAlign: 'center' }}>PF</th>
                <th style={{ textAlign: 'center' }}>PA</th>
                <th style={{ textAlign: 'center' }}>+/-</th>
                <th style={{ textAlign: 'center' }}>M</th>
                <th style={{ textAlign: 'center' }}>SO</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => (
                <tr key={entry.playerId}>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && index + 1}
                  </td>
                  <td>
                    <strong>{entry.playerName}</strong>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${getSkillBadgeClass(entry.skill)}`}>
                      {getSkillLabel(entry.skill)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>{entry.wins}</td>
                  <td style={{ textAlign: 'center' }}>{entry.losses}</td>
                  <td style={{ textAlign: 'center' }}>{entry.totalPoints}</td>
                  <td style={{ textAlign: 'center' }}>{entry.pointsAgainst}</td>
                  <td style={{ 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: entry.pointDifferential > 0 ? '#27ae60' : 
                           entry.pointDifferential < 0 ? '#e74c3c' : '#666'
                  }}>
                    {entry.pointDifferential > 0 ? '+' : ''}{entry.pointDifferential}
                  </td>
                  <td style={{ textAlign: 'center' }}>{entry.matchesPlayed}</td>
                  <td style={{ textAlign: 'center' }}>{entry.timesSatOut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {leaderboard.length === 0 && (
          <p className="text-center">No matches completed yet.</p>
        )}
      </div>

      <div className="card">
        <h3>Statistics Summary</h3>
        <div className="grid grid-3">
          <div className="card text-center">
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>
              {tournament.schedule.reduce((acc, round) => 
                acc + round.matches.filter(m => m.isComplete).length, 0
              )}
            </div>
            <div>Matches Completed</div>
          </div>

          <div className="card text-center">
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
              {tournament.schedule.reduce((acc, round) => 
                acc + round.matches.filter(m => !m.isComplete).length, 0
              )}
            </div>
            <div>Matches Remaining</div>
          </div>

          <div className="card text-center">
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e74c3c' }}>
              {tournament.players.length}
            </div>
            <div>Total Players</div>
          </div>
        </div>
      </div>

      {leaderboard.length > 0 && (
        <div className="card">
          <h3>Top Performers</h3>
          <div className="grid grid-3">
            {leaderboard.slice(0, 3).map((entry, index) => (
              <div 
                key={entry.playerId} 
                className="card text-center"
                style={{
                  background: index === 0 ? '#fff9e6' : 
                             index === 1 ? '#f5f5f5' : 
                             index === 2 ? '#fdf2f2' : 'white',
                  border: index === 0 ? '2px solid #f39c12' : 
                          index === 1 ? '2px solid #95a5a6' : 
                          index === 2 ? '2px solid #cd7f32' : '1px solid #ddd'
                }}
              >
                <div style={{ fontSize: '3rem' }}>
                  {index === 0 && '🥇'}
                  {index === 1 && '🥈'}
                  {index === 2 && '🥉'}
                </div>
                <h3 style={{ margin: '0.5rem 0' }}>{entry.playerName}</h3>
                <span className={`badge ${getSkillBadgeClass(entry.skill)}`}>
                  {getSkillLabel(entry.skill)}
                </span>
                <div className="mt-2">
                  <strong>{entry.wins}W - {entry.losses}L</strong>
                  <br />
                  <small>+/- {entry.pointDifferential > 0 ? '+' : ''}{entry.pointDifferential}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}